import { useRef, useState } from "react";
import { AdviceTake, DeptPage } from "./dept";
import { FigmaDesigns } from "./figma";
import { GitHubView, MilestoneBar } from "./github";
import { Avatar, ago, api, can, Drawer, Empty, fmtDate, go, Loading, Pill, setQuery, useLoad, useMe, useRoute } from "./lib";

export const WORK_STATUS: Record<string, { label: string; tone: string }> = {
  backlog: { label: "Backlog", tone: "" }, planned: { label: "Planned", tone: "" }, in_dev: { label: "In development", tone: "info" },
  in_testing: { label: "Testing", tone: "info" }, in_review: { label: "In review", tone: "warn" }, approved: { label: "Signed off", tone: "good" },
  released: { label: "Released", tone: "good" }, wont_do: { label: "Won't do", tone: "" },
};
const RELEASE_STATUS: Record<string, string> = {
  planned: "Planned", in_testing: "Testing", submitted: "Submitted to store", approved: "Store approved", live: "Live (verified)", delayed: "Delayed", cancelled: "Cancelled",
};
const PLATFORM: Record<string, string> = { ios: "iOS", android: "Android" };
const URL_LABEL: Record<string, string> = { design: "Design", repo: "Repository", issue: "Issue", pr: "Pull request", build: "Build", tests: "Test results" };

export function ReleaseDate({ r }: { r: any }) {
  if (r.is_live_verified) return <Pill tone="good">Live {fmtDate(r.verified_live_on)}</Pill>;
  return <Pill tone={r.status === "delayed" ? "warn" : ""}>{r.target_on ? `Target ${fmtDate(r.target_on)}` : "No target date"}</Pill>;
}

export function Product() {
  const { me } = useMe();
  const route = useRoute();
  const tab = route.q.get("tab") || "releases";
  const { data, error, reload } = useLoad<any>("/api/product", [route.q.get("release"), route.q.get("item")]);
  const [adding, setAdding] = useState<"" | "release" | "item">("");
  if (!data) return <div className="page"><Loading error={error} /></div>;
  const trains = [...new Set(data.releases.map((r: any) => r.train))] as string[];
  const take = (
    <AdviceTake
      page="product"
      ask="Ask Claude where to focus"
      intro="Claude reads the releases, the roadmap and product tasks here, then tells you what has to happen this week, what's at risk and what to let go. About 10 cents a run."
    />
  );
  const toolbar = (
      <div className="row between">
        <div className="tabs">
          <button className={tab === "releases" ? "on" : ""} onClick={() => setQuery({ tab: null })}>Releases</button>
          <button className={tab === "roadmap" ? "on" : ""} onClick={() => setQuery({ tab: "roadmap" })}>Roadmap</button>
          <button className={tab === "github" ? "on" : ""} onClick={() => setQuery({ tab: "github" })}>GitHub</button>
          <button className={tab === "designs" ? "on" : ""} onClick={() => setQuery({ tab: "designs" })}>Designs</button>
        </div>
        {data.can_edit && (
          <div className="row">
            <button className="btn" onClick={() => setAdding("item")}>New feature or bug</button>
            <button className="btn primary" onClick={() => setAdding("release")}>New release</button>
          </div>
        )}
      </div>
  );
  return (
    <DeptPage area="product" take={take} toolbar={toolbar}>
      {tab === "designs" ? (
        <FigmaDesigns area="product" />
      ) : tab === "github" ? (
        <GitHubView />
      ) : tab === "releases" ? (
        trains.length ? (
          <div className="grid">
            {trains.map((t) => (
              <section key={t} className="card stack">
                <h2>Version {t}</h2>
                {data.releases.filter((r: any) => r.train === t).map((r: any) => (
                  <div key={r.id} className="item clickable" onClick={() => setQuery({ release: r.id })}>
                    <div className="grow">
                      <div className="title">{PLATFORM[r.platform]}</div>
                      <div className="small muted">{RELEASE_STATUS[r.status]} · ready {r.readiness.done}/{r.readiness.total}</div>
                    </div>
                    {r.open_flags > 0 && <Pill tone="warn">{r.open_flags} to review</Pill>}
                    <ReleaseDate r={r} />
                  </div>
                ))}
              </section>
            ))}
          </div>
        ) : (
          <Empty>No releases yet. Add one to start tracking iOS and Android readiness.</Empty>
        )
      ) : (
        <div className="board">
          {Object.entries(WORK_STATUS).filter(([k]) => k !== "wont_do").map(([k, v]) => {
            const items = data.items.filter((w: any) => w.status === k);
            return (
              <div key={k} className="col">
                <h3>{v.label}<span className="muted">{items.length}</span></h3>
                {items.map((w: any) => (
                  <div key={w.id} className="tile" onClick={() => setQuery({ item: w.id })}>
                    <div className="row between" style={{ gap: 6 }}><strong className="small">{w.title}</strong>{w.kind === "bug" && <Pill tone="bad">Bug</Pill>}</div>
                    <div className="tiny muted">{w.platforms.map((p: string) => PLATFORM[p]).join(" + ")}{w.release_train ? ` · ${w.release_train}` : ""}{w.owner_name ? ` · ${w.owner_name}` : ""}</div>
                    {w.target_on && <div className="tiny muted">Target {fmtDate(w.target_on)}</div>}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
      {route.q.get("release") && <ReleaseDrawer id={route.q.get("release")!} onClose={() => setQuery({ release: null })} onChange={reload} />}
      {route.q.get("item") && <ItemDrawer id={route.q.get("item")!} trains={trains} onClose={() => setQuery({ item: null })} onChange={reload} />}
      {adding === "release" && <NewRelease onClose={() => setAdding("")} onSaved={() => (setAdding(""), reload())} />}
      {adding === "item" && <ItemForm trains={trains} onClose={() => setAdding("")} onSaved={(id) => (setAdding(""), reload(), setQuery({ tab: "roadmap", item: id }))} />}
      {!can(me, "product.edit") && <p className="muted small">You can see releases and the roadmap. Product owners make changes.</p>}
    </DeptPage>
  );
}

function NewRelease({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { toast } = useMe();
  return (
    <Drawer title="New release" onClose={onClose}>
      <form
        className="form"
        onSubmit={async (e) => {
          e.preventDefault();
          const fd = new FormData(e.currentTarget);
          try {
            await api("/api/releases", { method: "POST", body: { train: fd.get("train"), platforms: fd.getAll("platform"), target_on: fd.get("target_on"), notes: fd.get("notes") } });
            toast("Release added");
            onSaved();
          } catch (err: any) {
            toast(err.message, true);
          }
        }}
      >
        <label className="field">Version<input className="input" name="train" required placeholder="1.2.0" /></label>
        <div className="row">
          <label className="check"><input type="checkbox" name="platform" value="ios" defaultChecked /> iOS</label>
          <label className="check"><input type="checkbox" name="platform" value="android" defaultChecked /> Android</label>
        </div>
        <p className="muted small" style={{ margin: 0 }}>Each platform gets its own release, readiness checklist and live date.</p>
        <label className="field">Target date<input className="input" type="date" name="target_on" /></label>
        <label className="field">Release notes<textarea className="input" name="notes" /></label>
        <div className="row"><button className="btn primary">Add release</button></div>
      </form>
    </Drawer>
  );
}

const TYPE_HREF: Record<string, (id: string) => string> = {
  content: (id) => `#/marketing?content=${id}`, task: (id) => `#/tasks?task=${id}`, campaign: (id) => `#/marketing?tab=campaigns&campaign=${id}`,
  organization: (id) => `#/partnerships?org=${id}`, expense: () => `#/finance?tab=spending`,
};
const TYPE_LABEL: Record<string, string> = { content: "Content", task: "Task", campaign: "Campaign", organization: "Partner", expense: "Spending" };

function ReleaseDrawer({ id, onClose, onChange }: { id: string; onClose: () => void; onChange: () => void }) {
  const { toast } = useMe();
  const { data: r, error, reload } = useLoad<any>(`/api/releases/${id}`, [id]);
  const [busy, setBusy] = useState(false);
  const [live, setLive] = useState(false);
  const dateRef = useRef<HTMLInputElement>(null);
  if (!r) return <Drawer title="Release" onClose={onClose}><Loading error={error} /></Drawer>;
  const patch = async (body: any, ok: string) => {
    setBusy(true);
    try {
      const res = await api(`/api/releases/${id}`, { method: "PATCH", body });
      toast(res.flagged ? `${ok}. ${res.flagged} linked item${res.flagged === 1 ? "" : "s"} flagged for review; nothing was moved.` : ok);
      reload();
      onChange();
    } catch (e: any) {
      toast(e.message, true);
    } finally {
      setBusy(false);
    }
  };
  const openFlags = r.flags.filter((f: any) => f.status === "open");
  return (
    <Drawer title={r.name} onClose={onClose} sub={<div className="row"><Pill>{RELEASE_STATUS[r.status]}</Pill><ReleaseDate r={r} /></div>}>
      {openFlags.length > 0 && <div className="banner warn">{openFlags.length} linked item{openFlags.length === 1 ? "" : "s"} still need review after the date change.</div>}
      <section className="card stack">
        <h2>Dates</h2>
        <div className="form-2">
          <label className="field">Target date
            <input ref={dateRef} className="input" type="date" disabled={!r.can_edit || busy} defaultValue={r.target_on || ""} key={r.target_on} />
          </label>
          <div className="field">Verified live
            <div className="small">{r.is_live_verified ? <>{fmtDate(r.verified_live_on)} · {r.verified_source}</> : <span className="muted">Not verified yet. A target date never counts as live.</span>}</div>
          </div>
        </div>
        {r.can_edit && (
          <div className="row">
            <button
              className="btn"
              disabled={busy}
              onClick={() => {
                const v = dateRef.current?.value || "";
                if (v === (r.target_on || "")) return;
                if (r.linked.length && !confirm(`Move the target date? ${r.linked.length} linked item${r.linked.length === 1 ? "" : "s"} will be flagged for their owners to review. Nothing linked will be moved.`)) return;
                patch({ target_on: v || null }, "Target date changed");
              }}
            >
              Save target date
            </button>
            {!r.is_live_verified && <button className="btn" onClick={() => setLive(true)}>Record as live</button>}
            <select className="input" style={{ width: "auto" }} value={r.status} disabled={busy} onChange={(e) => patch({ status: e.target.value }, "Status changed")}>
              {Object.entries(RELEASE_STATUS).filter(([k]) => k !== "live" || r.is_live_verified).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
        )}
        {live && (
          <form className="form" onSubmit={(e) => { e.preventDefault(); const fd = new FormData(e.currentTarget); patch({ verified_live_on: fd.get("d"), verified_source: fd.get("s") }, "Recorded as live").then(() => setLive(false)); }}>
            <div className="form-2">
              <label className="field">Live since<input className="input" type="date" name="d" required /></label>
              <label className="field">What confirmed it<input className="input" name="s" required placeholder={r.platform === "ios" ? "App Store Connect: Ready for Distribution" : "Play Console: Available on Google Play"} /></label>
            </div>
            <div className="row"><button className="btn primary">Save</button></div>
          </form>
        )}
      </section>

      {r.github && (
        <section className="card stack">
          <div className="row between"><h2>On GitHub: milestone {r.github.title}</h2><a className="small" href={r.github.url} target="_blank" rel="noreferrer">Open</a></div>
          <MilestoneBar m={r.github} />
        </section>
      )}
      <section className="card stack">
        <h2>Readiness <span className="muted small">{r.readiness.done}/{r.readiness.total}</span></h2>
        {r.checks.map((c: any) => (
          <label key={c.id} className="check" style={{ justifyContent: "space-between", display: "flex" }}>
            <span className="row" style={{ gap: 8 }}>
              <input type="checkbox" disabled={!r.can_edit} checked={!!c.done_at} onChange={async (e) => { await api(`/api/releases/${id}/checks/${c.id}`, { method: "POST", body: { done: e.target.checked } }); reload(); onChange(); }} />
              {c.label}
            </span>
            {c.done_at && <span className="muted tiny">{c.done_by_name}, {ago(c.done_at)}</span>}
          </label>
        ))}
      </section>

      <section className="card stack">
        <h2>Linked work</h2>
        <p className="sub" style={{ margin: 0 }}>Content, tasks, campaigns, partners and spending that depend on this release. Link them from their own pages.</p>
        {r.linked.length ? (
          <div className="list">
            {r.linked.map((l: any) => {
              const flag = openFlags.find((f: any) => f.record_type === l.from_type && f.record_id === l.from_id);
              return (
                <a key={l.from_id} className="item clickable" href={TYPE_HREF[l.from_type]!(l.from_id)} style={{ color: "inherit", textDecoration: "none" }}>
                  <div className="grow"><div className="title">{l.label}</div><div className="small muted">{TYPE_LABEL[l.from_type]}</div></div>
                  {flag && <Pill tone="warn">Needs review</Pill>}
                </a>
              );
            })}
          </div>
        ) : <span className="muted small">Nothing linked yet.</span>}
      </section>

      <section className="card stack">
        <h2>In this release</h2>
        {r.items.length ? r.items.map((w: any) => (
          <a key={w.id} className="item clickable" href={`#/product?tab=roadmap&item=${w.id}`} style={{ color: "inherit", textDecoration: "none" }}>
            <span className="grow title">{w.title}</span>
            <Pill tone={WORK_STATUS[w.status]!.tone}>{WORK_STATUS[w.status]!.label}</Pill>
          </a>
        )) : <span className="muted small">No roadmap items set to version {r.train} for {PLATFORM[r.platform]}.</span>}
      </section>

      <section className="card stack">
        <h2>Release notes and build</h2>
        <textarea className="input" disabled={!r.can_edit} defaultValue={r.notes} onBlur={(e) => e.target.value !== r.notes && patch({ notes: e.target.value }, "Notes saved")} />
        <input className="input" type="url" disabled={!r.can_edit} placeholder="Build link (TestFlight, Play Console)" defaultValue={r.build_url || ""} onBlur={(e) => e.target.value !== (r.build_url || "") && patch({ build_url: e.target.value }, "Build link saved")} />
      </section>

      {r.flags.length > 0 && (
        <section className="card stack">
          <h2>Date-change flags</h2>
          <div className="timeline">
            {r.flags.map((f: any) => (
              <div key={f.id}>
                <time>{ago(f.created_at)}</time>
                <span>{f.record?.label}: {f.old_date ?? "no date"} → {f.new_date ?? "no date"} · {f.status === "open" ? "open" : `resolved: ${f.resolution}`}</span>
              </div>
            ))}
          </div>
        </section>
      )}
      <section className="card stack">
        <h2>History</h2>
        <div className="timeline">{r.activity.map((a: any) => <div key={a.id}><time>{ago(a.created_at)}</time><span>{a.actor_name || "System"}: {a.action.replace(/^release\./, "").replace(/_/g, " ")}{a.detail.flagged ? ` (${a.detail.flagged} flagged)` : ""}</span></div>)}</div>
      </section>
    </Drawer>
  );
}

function ItemForm({ item, trains, onClose, onSaved }: { item?: any; trains: string[]; onClose: () => void; onSaved: (id: string) => void }) {
  const { toast } = useMe();
  const users = useLoad<any[]>("/api/users").data || [];
  const w = item || { kind: "feature", priority: "normal", platforms: ["ios", "android"], urls: {} };
  return (
    <Drawer title={item ? `Edit ${item.title}` : "New feature or bug"} onClose={onClose}>
      <form
        className="form"
        onSubmit={async (e) => {
          e.preventDefault();
          const fd = new FormData(e.currentTarget);
          const body = {
            title: fd.get("title"), kind: fd.get("kind"), priority: fd.get("priority"), platforms: fd.getAll("platforms"), owner_id: fd.get("owner_id"),
            estimate: fd.get("estimate"), target_on: fd.get("target_on"), release_train: fd.get("release_train"), description: fd.get("description"),
            urls: Object.fromEntries(Object.keys(URL_LABEL).map((k) => [k, fd.get("url_" + k)])),
          };
          try {
            const r = item ? await api(`/api/work-items/${item.id}`, { method: "PATCH", body }) : await api("/api/work-items", { method: "POST", body });
            toast("Saved");
            onSaved(item?.id ?? r.id);
          } catch (err: any) {
            toast(err.message, true);
          }
        }}
      >
        <label className="field">Title<input className="input" name="title" required defaultValue={w.title} /></label>
        <div className="form-2">
          <label className="field">Type<select className="input" name="kind" defaultValue={w.kind}><option value="feature">Feature</option><option value="bug">Bug</option><option value="request">Request</option></select></label>
          <label className="field">Priority<select className="input" name="priority" defaultValue={w.priority}><option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option></select></label>
          <label className="field">Owner<select className="input" name="owner_id" defaultValue={w.owner_id || ""}><option value="">No owner</option>{users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select></label>
          <label className="field">Estimate<input className="input" name="estimate" defaultValue={w.estimate || ""} placeholder="e.g. 3 days" /></label>
          <label className="field">Target date<input className="input" type="date" name="target_on" defaultValue={w.target_on || ""} /></label>
          <label className="field">Version
            <input className="input" name="release_train" list="trains" defaultValue={w.release_train || ""} />
            <datalist id="trains">{trains.map((t) => <option key={t} value={t} />)}</datalist>
          </label>
        </div>
        <div className="row">
          <label className="check"><input type="checkbox" name="platforms" value="ios" defaultChecked={w.platforms.includes("ios")} /> iOS</label>
          <label className="check"><input type="checkbox" name="platforms" value="android" defaultChecked={w.platforms.includes("android")} /> Android</label>
        </div>
        <label className="field">Details<textarea className="input" name="description" defaultValue={w.description || ""} /></label>
        <div className="form-2">
          {Object.entries(URL_LABEL).map(([k, l]) => <label key={k} className="field">{l} link<input className="input" type="url" name={"url_" + k} defaultValue={w.urls[k] || ""} placeholder="https://" /></label>)}
        </div>
        <div className="row"><button className="btn primary">Save</button></div>
      </form>
    </Drawer>
  );
}

function ItemDrawer({ id, trains, onClose, onChange }: { id: string; trains: string[]; onClose: () => void; onChange: () => void }) {
  const { toast } = useMe();
  const { data: w, error, reload } = useLoad<any>(`/api/work-items/${id}`, [id]);
  const [edit, setEdit] = useState(false);
  const [note, setNote] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);
  if (!w) return <Drawer title="Item" onClose={onClose}><Loading error={error} /></Drawer>;
  const done = () => (reload(), onChange());
  const run = async (fn: () => Promise<any>, ok: string) => {
    try {
      await fn();
      toast(ok);
      done();
    } catch (e: any) {
      toast(e.message, true);
    }
  };
  return (
    <Drawer title={w.title} onClose={onClose} sub={<div className="row"><Pill tone={WORK_STATUS[w.status]!.tone}>{WORK_STATUS[w.status]!.label}</Pill>{w.kind === "bug" && <Pill tone="bad">Bug</Pill>}<span className="muted small">{w.platforms.map((p: string) => PLATFORM[p]).join(" + ")}</span></div>}>
      {w.status === "approved" && <div className="banner good">Signed off by {w.signed_off_by_name}, {ago(w.signed_off_at)}.</div>}
      {w.status === "in_review" && w.can_approve && (
        <section className="card stack">
          <h2>Review this work</h2>
          <textarea className="input" style={{ minHeight: 64 }} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What needs fixing (needed to send it back)" />
          <div className="row">
            <button className="btn primary" onClick={() => run(() => api(`/api/work-items/${id}/review`, { method: "POST", body: { decision: "approve", note } }), "Signed off").then(() => setNote(""))}>Approve</button>
            <button className="btn" disabled={!note.trim()} onClick={() => run(() => api(`/api/work-items/${id}/review`, { method: "POST", body: { decision: "request_fix", note } }), "Sent back with fixes").then(() => setNote(""))}>Request fixes</button>
          </div>
        </section>
      )}
      <section className="card stack">
        <div className="row between"><h2>Details</h2>{w.can_edit && <button className="btn sm" onClick={() => setEdit(true)}>Edit</button>}</div>
        {w.can_edit && (
          <label className="field">Status
            <select className="input" value={w.status} onChange={(e) => run(() => api(`/api/work-items/${id}`, { method: "PATCH", body: { status: e.target.value } }), "Status changed")}>
              {Object.entries(WORK_STATUS).filter(([k]) => k !== "approved" || w.status === "approved").map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </label>
        )}
        <div className="form-2 small">
          <div><div className="muted tiny">Owner</div>{w.owner_name || "No owner"}</div>
          <div><div className="muted tiny">Estimate</div>{w.estimate || "None"}</div>
          <div><div className="muted tiny">Target</div>{w.target_on ? fmtDate(w.target_on) : "Not set"}</div>
          <div><div className="muted tiny">Version</div>{w.release_train || "Not assigned"}</div>
        </div>
        {w.description && <p style={{ margin: 0, whiteSpace: "pre-wrap" }}>{w.description}</p>}
        {Object.keys(w.urls).length > 0 && <div className="row">{Object.entries(w.urls).map(([k, u]) => <a key={k} className="pill" href={u as string} target="_blank" rel="noreferrer">{URL_LABEL[k]}</a>)}</div>}
        {w.releases.length > 0 && <div className="row">{w.releases.map((r: any) => <a key={r.id} href={`#/product?release=${r.id}`} className="pill">{r.name} · {r.is_live_verified ? "live" : r.target_on ? `target ${fmtDate(r.target_on)}` : "no date"}</a>)}</div>}
      </section>
      <section className="card stack">
        <div className="row between">
          <h2>Screenshots and recordings</h2>
          {w.can_edit && (
            <>
              <input ref={fileRef} type="file" multiple accept="image/*,video/mp4,video/quicktime,application/pdf" style={{ display: "none" }} onChange={async (e) => {
                const files = [...(e.target.files || [])];
                if (!files.length) return;
                const fd = new FormData();
                fd.set("record_type", "work_item");
                fd.set("record_id", id);
                files.forEach((f) => fd.append("file", f));
                await run(() => api("/api/attachments", { method: "POST", body: fd }), "Uploaded");
                e.target.value = "";
              }} />
              <button className="btn sm" onClick={() => fileRef.current?.click()}>Upload</button>
            </>
          )}
        </div>
        {w.attachments.length ? (
          <div className="assets">
            {w.attachments.map((a: any) => (
              <a key={a.id} className="asset" href={`/api/attachments/${a.id}`} target="_blank" rel="noreferrer">
                {a.mime.startsWith("image/") ? <img src={`/api/attachments/${a.id}`} alt={a.filename} /> : a.mime.startsWith("video/") ? <video src={`/api/attachments/${a.id}`} muted preload="metadata" /> : <div className="file-doc">PDF</div>}
                <div className="meta"><span>{a.filename}</span></div>
              </a>
            ))}
          </div>
        ) : <span className="muted small">None yet.</span>}
      </section>
      <section className="card stack">
        <h2>Feedback</h2>
        {w.comments.map((c: any) => <div key={c.id} className="comment"><div className="who row" style={{ gap: 8 }}><Avatar id={c.author_id} name={c.author_name} hasPhoto={c.author_has_photo} size={22} />{c.author_name} <span className="muted tiny">{ago(c.created_at)}</span></div><p>{c.body}</p></div>)}
        <form className="row" onSubmit={async (e) => { e.preventDefault(); if (!note.trim()) return; await run(() => api("/api/comments", { method: "POST", body: { record_type: "work_item", record_id: id, body: note } }), "Comment posted"); setNote(""); }}>
          <input className="input" style={{ flex: 1 }} value={w.status === "in_review" && w.can_approve ? "" : note} disabled={w.status === "in_review" && w.can_approve} onChange={(e) => setNote(e.target.value)} placeholder={w.status === "in_review" && w.can_approve ? "Use the review box above" : "Add feedback"} />
          <button className="btn">Post</button>
        </form>
      </section>
      <section className="card stack">
        <h2>History</h2>
        <div className="timeline">{w.activity.map((a: any) => <div key={a.id}><time>{ago(a.created_at)}</time><span>{a.actor_name || "System"}: {a.action.replace(/^work_item\./, "").replace(/_/g, " ")}</span></div>)}</div>
      </section>
      {edit && <ItemForm item={w} trains={trains} onClose={() => setEdit(false)} onSaved={() => (setEdit(false), done())} />}
    </Drawer>
  );
}

// Lets any linked page say "this depends on release X". Changing that release's date flags it.
export function ReleaseLink({ type, id, canEdit }: { type: string; id: string; canEdit: boolean }) {
  const { me, toast } = useMe();
  const { data } = useLoad<any>(can(me, "product.view") ? "/api/product" : null);
  const [linked, setLinked] = useState<string | null>(null);
  if (!data || !canEdit || !data.releases.length) return null;
  return (
    <div className="row small">
      <span className="muted">Depends on a release?</span>
      <select
        className="input"
        style={{ width: "auto" }}
        value=""
        onChange={async (e) => {
          const rid = e.target.value;
          if (!rid) return;
          try {
            await api("/api/links", { method: "POST", body: { from_type: type, from_id: id, to_type: "release", to_id: rid } });
            setLinked(data.releases.find((r: any) => r.id === rid)?.name);
            toast("Linked. You'll be told if its date moves.");
          } catch (err: any) {
            toast(err.message, true);
          }
        }}
      >
        <option value="">Link a release…</option>
        {data.releases.filter((r: any) => !r.is_live_verified).map((r: any) => <option key={r.id} value={r.id}>{r.name}{r.target_on ? ` (target ${fmtDate(r.target_on)})` : ""}</option>)}
      </select>
      {linked && <Pill tone="info">Linked to {linked}</Pill>}
    </div>
  );
}

// Open release-date flags, with a place to record the decision.
export function ReleaseFlags({ flags, onResolved }: { flags: any[]; onResolved: () => void }) {
  const { toast } = useMe();
  const [text, setText] = useState<Record<string, string>>({});
  if (!flags.length) return null;
  return (
    <section className="card stack">
      <h2>Release dates moved: please review</h2>
      <p className="sub" style={{ margin: 0 }}>These depend on a release whose target date changed. Nothing was moved for you.</p>
      {flags.map((f) => (
        <div key={f.id} className="stack" style={{ gap: 6, paddingBottom: 10, borderBottom: "1px solid var(--line)" }}>
          <div className="row between">
            <a href={TYPE_HREF[f.record_type]!(f.record_id)}><strong>{f.record?.label}</strong></a>
            <span className="small muted">{f.release_name}: {f.old_date ? fmtDate(f.old_date) : "no date"} → {f.new_date ? fmtDate(f.new_date) : "no date"}</span>
          </div>
          <form
            className="row"
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                await api(`/api/flags/${f.id}/resolve`, { method: "POST", body: { resolution: text[f.id] } });
                toast("Marked as reviewed");
                onResolved();
              } catch (err: any) {
                toast(err.message, true);
              }
            }}
          >
            <input className="input" style={{ flex: 1 }} placeholder="What you decided (e.g. moved the post to Dec 20)" value={text[f.id] || ""} onChange={(e) => setText({ ...text, [f.id]: e.target.value })} />
            <button className="btn sm">Done</button>
          </form>
        </div>
      ))}
    </section>
  );
}

export const goRelease = (id: string) => go("/product", { release: id });
